import type { CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';
import { UnstyledButton, Text } from '@/shared/ui';

export type TabId = 'dados' | 'descricao';

interface TabNavigationProps {
  activeTab: TabId;
  onChange: (tab: TabId) => void;
  erroredTabs?: TabId[];
}

const TAB_IDS: TabId[] = ['dados', 'descricao'];

const containerStyle: CSSProperties = {
  display: 'flex',
  borderBottom: '1px solid var(--border-subtle)',
  marginBottom: 20,
  gap: 0,
};

/**
 * Resolves the tab label color for the current active/error state.
 *
 * @param active - Whether the tab is currently selected
 * @param hasError - Whether the tab has a validation error
 * @returns A CSS color value
 */
function tabColor(active: boolean, hasError: boolean): string {
  if (hasError) return 'var(--status-error)';
  return active ? 'var(--seed-base)' : 'var(--text-secondary)';
}

/**
 * Resolves the tab bottom-border style for the current active/error state.
 *
 * @param active - Whether the tab is currently selected
 * @param hasError - Whether the tab has a validation error
 * @returns A CSS border-bottom value
 */
function tabBorder(active: boolean, hasError: boolean): string {
  if (!active) return '2px solid transparent';
  return `2px solid ${hasError ? 'var(--status-error)' : 'var(--seed-base)'}`;
}

/**
 * Builds the inline style for a single navigation tab.
 *
 * @param active - Whether the tab is currently selected
 * @param hasError - Whether the tab has a validation error
 * @returns The CSS properties for the tab button
 */
function tabStyle(active: boolean, hasError: boolean): CSSProperties {
  return {
    flex: 1,
    padding: '10px 0',
    fontSize: 13,
    fontWeight: 600,
    fontFamily: 'var(--font-family)',
    color: tabColor(active, hasError),
    background: 'none',
    border: 'none',
    borderBottom: tabBorder(active, hasError),
    cursor: 'pointer',
    transition: 'all 0.15s ease',
    textAlign: 'center',
  };
}

const errorDotStyle: CSSProperties = {
  display: 'inline-block',
  width: 6,
  height: 6,
  marginLeft: 6,
  borderRadius: '50%',
  background: 'var(--status-error)',
  verticalAlign: 'middle',
};

/**
 * Renders the property drawer tab bar with active and error indicators.
 *
 * @param activeTab - The currently selected tab
 * @param onChange - Invoked with the tab id when a tab is clicked
 * @param erroredTabs - Tabs that currently have validation errors
 */
export function TabNavigation({ activeTab, onChange, erroredTabs = [] }: TabNavigationProps) {
  const { t } = useTranslation('features/properties');

  return (
    <div style={containerStyle} role="tablist">
      {TAB_IDS.map((id) => {
        const hasError = erroredTabs.includes(id);
        return (
          <UnstyledButton
            key={id}
            type="button"
            role="tab"
            aria-selected={activeTab === id}
            data-has-error={hasError ? 'true' : 'false'}
            onClick={() => onChange(id)}
            style={tabStyle(activeTab === id, hasError)}
          >
            {t(`form.drawer.tabs.${id}`)}
            {hasError && <Text as="span" style={errorDotStyle} aria-hidden="true" />}
          </UnstyledButton>
        );
      })}
    </div>
  );
}
